import { useTheme } from "@emotion/react";
import React, { useEffect, useRef, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";

import { Priority } from "@megaron/support-contracts";

type Props = {
  value: Priority | null | undefined;
  isDisabled?: boolean;
  onChange: (priority: Priority | null) => void;
};

const priorityLabels: Record<Priority, string> = {
  low: "Niski",
  medium: "Średni",
  high: "Wysoki",
  urgent: "Pilny",
};

const priorityOptions: (Priority | null)[] = [null, "low", "medium", "high", "urgent"];

const priorityColor = (
  priority: Priority | null | undefined,
  colors: { success: string; warning: string; danger: string; primary: string; secondaryText: string },
) => {
  if (priority === "low") return colors.success;
  if (priority === "medium") return colors.warning;
  if (priority === "high") return colors.danger;
  if (priority === "urgent") return colors.primary;
  return colors.secondaryText;
};

export const SupportPrioritySelect: React.FC<Props> = ({ value, onChange, isDisabled }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      const path = event.composedPath();
      const root = dropdownRef.current;
      if (root && !path.includes(root)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  const currentLabel = value ? priorityLabels[value] : "Ustaw priorytet";
  const currentColor = priorityColor(value, theme.colors);

  return (
    <div ref={dropdownRef} css={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => !isDisabled && setIsOpen((prev) => !prev)}
        css={{
          borderRadius: theme.smallBorderRadius,
          display: "flex",
          padding: "10px 12px",
          justifyContent: "space-between",
          border: `1px solid ${theme.colors.border}`,
          alignItems: "center",
          fontSize: "14px",
          fontWeight: 700,
          cursor: isDisabled ? "not-allowed" : "pointer",
          width: "100%",
          gap: "8px",
          background: "white",
          opacity: isDisabled ? 0.7 : 1,
        }}
      >
        <span css={{ color: currentColor, display: "flex", gap: "8px", alignItems: "center" }}>
          <span
            css={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: currentColor,
              flexShrink: 0,
            }}
          />
          {currentLabel}
        </span>
        <MdKeyboardArrowDown size={18} color={theme.colors.secondaryText} />
      </button>
      {isOpen && !isDisabled && (
        <div
          css={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            width: "100%",
            borderRadius: theme.smallBorderRadius,
            boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.2)",
            padding: "0.25rem 0",
            backgroundColor: "white",
            zIndex: 2,
          }}
        >
          {priorityOptions.map((priority) => {
            const optionLabel = priority ? priorityLabels[priority] : "Ustaw priorytet";
            const optionColor = priorityColor(priority, theme.colors);
            return (
              <button
                key={priority ?? "none"}
                type="button"
                onClick={() => {
                  onChange(priority);
                  setIsOpen(false);
                }}
                css={{
                  width: "100%",
                  padding: "0.5rem 0.75rem",
                  cursor: "pointer",
                  border: "none",
                  background: "transparent",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: optionColor,
                  fontWeight: 600,
                  "&:hover": { background: theme.colors.hover },
                }}
              >
                <span
                  css={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: optionColor,
                    flexShrink: 0,
                  }}
                />
                {optionLabel}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
